import React, { useState, useCallback, useRef, useEffect, createRef } from 'react';
import { SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import styles, { getModernCardStyle, getModernTextColor } from '@stylesheet';
import { Image, Alert, FlatList, TextInput, ScrollView, Platform } from 'react-native';
import { Text, View, Title, Card } from '@constants/Themed';
import LottieView from '@utils/lottieAnimation';
import { Pressable } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { ADDED_DATA_TO_STORE, CHANGE_DATA, CHANGE_DATA_TITLE, CLEAR_HANDLER_DATA } from '@reduxLocal/actions/types';
import { DefaultRootState, store } from '@reduxLocal/persistState';
import apis, { getGDriveFolderContent, getGDriveItem, noMonitor, trackDataView, trackDataInteraction } from '@api/api';
import { useTranslation } from 'react-i18next';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import filter from 'lodash.filter';
import AnimatedLottieView from '@utils/lottieAnimation';
import GlobalTutorial from '@components/TutorialComponent/GlobalTutorial';
import SchemaPreviewer from './components/SchemaPreviewer';
import { saveData } from "@api/api";
import TutorialPreview from './components/TutorialPreview';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import RobotHelper from '@components/RobotHelper/RobotHelper';
import FlipCard from "react-native-flip-card";
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import { FontAwesome } from '@expo/vector-icons';
import { AI_API_URL } from '@api/constant';
import parseSchema from '@navigation/NavigationStacks/utils/parseSchema';
import ActionButton from '@components/UI/SmallActionButton';
import { H5, H6, SizableText, XStack } from 'tamagui';
import useGlobalUser from '@hooks/getGlobalUser';
import { useDeviceType } from '@hooks/useDeviceType';
import { YStack } from 'tamagui';
import DatabaseConnectionForm from './components/DatabaseConnectionForm';
import DatabaseConnectionCard from './components/DatabaseConnectionCard';
import SchemaImportModal, { SchemaImportModalHandle } from '@components/modals/SchemaImportModal/SchemaImportModal';

const carouselData = [
  {
    title: 'Select a template to get started',
    caption: 'Get a lightning start with our AI enhanced templates, select a template to begin',
    image: <TutorialPreview indexLength={1} currentIndex={0} />
  },
  {
    title: 'Customise your data to suit your requirements',
    caption: 'You can manipulate, add or remove data fields to your liking',
    image: <TutorialPreview currentIndex={0} indexLength={0} />
  },
  {
    title: 'Then, simply save your data and distribute!',
    caption: "Don't worry, we will handle the reporting for you!",
    image: <AnimatedLottieView style={{ width: '40%', alignSelf: 'center', alignItems: 'center' }} source={require('@assets/animations/105897-saved-folder.json')} autoPlay />
  }
];

const CreateDataScreen = ({ navigation }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [info, setInfo] = useState({});
  const theme = themeSelector();
  const dataLoaded = useSelector((state: DefaultRootState) => state.app.data.dataImporter.dataHeld)
  let dataUploaded = useSelector((state: DefaultRootState) => state.uploadPreview.present.defaultData)
  const DataUpload = dataUploaded;
  const [defaultSchemas, setDefaultSchemas] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const schemasRef = useRef([]); // Ref to always have latest schemas for search
  const [query, setQuery] = useState('');
  const [selectedSchema, setSelectedSchema] = useState(false);
  const [selectedSchemaItem, setSelectedSchemaItem] = useState(null); // Track the full schema item
  const [searchFocused, setSearchFocused] = useState(false);
  const [currentTitle, setCurrentTitle] = useState('Data Preview')
  const dataTracker = useSelector((state: DefaultRootState) => state.uploadPreview.present);
  const [hideTutorial, setHideTutorial] = useState(false);
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [helpOpen, setHelpOpen] = useState(true);
  const dispatch = useDispatch();
  const [flip, setFlip] = useState(true);
  const id = useGlobalUser();
  const { isTablet } = useDeviceType();
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [screenData, setScreenData] = useState({ width: undefined, height: undefined });
  const [databaseConnections, setDatabaseConnections] = useState([]);
  const [showDatabaseForm, setShowDatabaseForm] = useState(false);
  const [editingConnection, setEditingConnection] = useState(null);
  const schemaImportRef = useRef<SchemaImportModalHandle>(null);
  const { status, data, error, isFetching, isLoading } = useQuery({
    queryKey: ['sampleSchemas'],
    queryFn: async () => {
      const data = await apis.getSampleSchemas();
      return data;
    },
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false
  })

  // Sync data from useQuery to local state
  useEffect(() => {
    if (data) {
      setSchemas(data);
      schemasRef.current = data; // Update ref with latest data
      setDefaultSchemas(data);
    }
  }, [data]);

  const loadDatabaseConnections = useCallback(async () => {
    try {
      const connections = await apis.getDatabaseConnections(undefined);
      setDatabaseConnections(connections || []);
    } catch (error) {
      console.error('Error loading database connections:', error);
    }
  }, []);

  useEffect(() => {
    //Clear any stored id in state once this page is loaded
    dispatch({ type: CLEAR_HANDLER_DATA });

    // Load database connections
    loadDatabaseConnections();

    return () => {
      queryClient.resetQueries({ queryKey: ['sampleSchemas'] });
    }
  }, [loadDatabaseConnections, dispatch, queryClient])

  const contains = useCallback(({ dataTags, schemaTitle }, query) => {
    // Convert dataTags to string if it's an array
    const tagsString = Array.isArray(dataTags) ? dataTags.join(' ') : (dataTags || '');

    // Use test() method of RegExp to check if dataTags or schemaTitle matches the query
    return query.test(tagsString) || query.test(schemaTitle || '');
  }, []);

  const handleSearch = useCallback((text) => {
    setQuery(text);

    // Get the latest schemas from ref to ensure we always have current data
    const currentSchemas = schemasRef.current?.length > 0 ? schemasRef.current : schemas;

    // If search is empty, restore all schemas from the original data
    if (text.trim() === '') {
      setDefaultSchemas(currentSchemas);
      return;
    }

    // Escape special regex characters to treat user input as literal text
    const escapedText = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const formattedQuery = new RegExp(escapedText, 'i'); // 'i' flag for case-insensitive search

    // Always filter from the original schemas array
    const filteredData = filter(currentSchemas, schema => {
      return contains(schema, formattedQuery);
    });

    setDefaultSchemas(filteredData);
  }, [schemas, contains]);

  const completeTutorialCallBack = () => {
    setHideTutorial(true);
  }

  // Enhanced schema selection with usage tracking
  const handleSchemaSelection = useCallback(async (item) => {
    try {
      // Track schema view - this counts as viewing the schema
      await trackDataView(item._id);
      // Refresh schema data to show updated stats
      queryClient.invalidateQueries({ queryKey: ['sampleSchemas'] });
    } catch (error) {
      console.warn('Failed to track schema view:', error);
      // Don't break user flow
    }

    setLoadingSchema(true);
    setCurrentTitle(item.schemaTitle);
    setSelectedSchema(true);
    setSelectedSchemaItem(item); // Store the full schema item
    setSearchFocused(false);
    dispatch({ type: CHANGE_DATA_TITLE, payload: item.schemaTitle || undefined });
    dispatch({ type: CHANGE_DATA, payload: item });
    setTimeout(() => setLoadingSchema(false), 1500);
  }, [dispatch, queryClient]);

  // Enhanced blank canvas selection (no tracking needed as it's not a database item)
  const handleBlankCanvasSelection = useCallback(async () => {
    dispatch({
      type: CHANGE_DATA,
      payload: {
        schemaTitle: ' ',
        dataTags: [' '],
        description: ' ',
        schemaContent: [{ title: 'first field', value: ' ', type: 'string' }]
      }
    });
    setCurrentTitle('Blank Canvas');
    setSelectedSchema(true);
    setSelectedSchemaItem(null); // No schema item for blank canvas
    setSearchFocused(false);
  }, [dispatch]);

  // Enhanced proceed to edit with usage tracking
  const handleProceedToEdit = useCallback(async () => {
    try {
      // Track when user proceeds to edit as an interaction
      if (selectedSchemaItem?._id) {
        await trackDataInteraction(selectedSchemaItem._id, 'edit_clicked');
        // Refresh schema data to show updated stats
        queryClient.invalidateQueries({ queryKey: ['sampleSchemas'] });
      }
    } catch (error) {
      console.warn('Failed to track edit interaction:', error);
    }

    navigation.navigate('DataHandler');
  }, [navigation, selectedSchemaItem, queryClient]);

  // Enhanced save action with usage tracking
  const handleSaveData = useCallback(async () => {
    try {
      // Track when user saves data as an interaction
      if (selectedSchemaItem?._id) {
        await trackDataInteraction(selectedSchemaItem._id, 'save');
        // Refresh schema data to show updated stats
        queryClient.invalidateQueries({ queryKey: ['sampleSchemas'] });
      }
    } catch (error) {
      console.warn('Failed to track save interaction:', error);
    }

    // Get company ID - handle both id and _id properties
    const companyId = currentCompany ? (currentCompany.id || currentCompany._id) : null;

    await saveData({
      id: id,
      company: companyId,
      data: {
        dataTitle: dataTracker.dataTitle,
        dataTags: dataTracker.dataTags,
        dataContent: null,
        dataSchema: dataTracker.dataHeld
      }
    }).then((res) => {
      if (res?.success) {
        dispatch({ type: ADDED_DATA_TO_STORE, log: true });
        navigation.goBack();
      }
    });
  }, [id, currentCompany, dataTracker, selectedSchemaItem, dispatch, navigation, queryClient]);

  // AI field suggestion handler
  const handleAIFieldSuggestion = useCallback(async () => {
    if (!dataTracker.dataHeld || dataTracker.dataHeld.length === 0) {
      return;
    }

    try {
      const currentFields = dataTracker.dataHeld.map(f => f.title);
      const response = await noMonitor.post('/ai/data-schema/suggest-fields', {
        partial_schema: { schemaContent: dataTracker.dataHeld },
        current_fields: currentFields
      });

      if (response.data.success && response.data.suggestions) {
        setAiSuggestions(response.data.suggestions);
        setShowAISuggestions(true);
      }
    } catch (error) {
      console.warn('Failed to get AI field suggestions:', error);
    }
  }, [dataTracker.dataHeld]);

  // AI schema validation handler
  const handleAIValidation = useCallback(async () => {
    if (!dataTracker.dataHeld || dataTracker.dataHeld.length === 0) {
      store.dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: 'Please add some fields to your schema first',
          type: 'warning'
        }
      });
      return;
    }

    try {
      const response = await noMonitor.post('/ai/data-schema/validate', {
        schema: {
          schemaTitle: dataTracker.dataTitle,
          schemaContent: dataTracker.dataHeld,
          dataTags: dataTracker.dataTags
        }
      });

      if (response.data.success && response.data.validation) {
        const validation = response.data.validation;

        if (validation.valid) {
          store.dispatch({
            type: SHOW_AUTH_MODAL,
            data: {
              content: `Schema is valid! Completeness: ${(validation.completeness_score * 100).toFixed(0)}%`,
              type: 'success'
            }
          });
        } else {
          const issuesText = validation.issues.join('\n');
          store.dispatch({
            type: SHOW_AUTH_MODAL,
            data: {
              content: `Validation Issues:\n${issuesText}`,
              type: 'error'
            }
          });
        }
      }
    } catch (error) {
      console.warn('Failed to validate schema:', error);
    }
  }, [dataTracker]);


  const headerItem = () => {
    const cardStyle = getModernCardStyle(theme, 'medium');

    const initialSchemas = [
      {
        "schemaTitle": "Products",
        "dataTags": [
          "product",
          "registration"
        ],
        "description": "Track your products and their details",
        "schemaContent": [
          {
            "title": "Product Name",
            "type": "string"
          },
          {
            "title": "Product Description",
            "type": "string",
            "ref": "multiline"
          },
          {
            "title": "Price",
            "type": "number"
          },
          {
            "title": "Serial Number",
            "type": "serialNumber"
          },
          {
            "title": "Purchase Date",
            "type": "date"
          },
          {
            "title": "Purchase Location",
            "type": "string"
          },
          {
            "title": "Proof of Purchase (Upload)",
            "type": "string"
          },
          {
            "title": "Warranty Code",
            "type": "string"
          },
          {
            "title": "Product Registration Date",
            "type": "date"
          },
          {
            "title": "Additional Accessories",
            "type": "string",
            "validation": {
              "ref": "multiline"
            }
          }
        ],
        "lastViewedAt": {
          "$date": "2025-09-16T03:23:51.145Z"
        },
        "updatedAt": {
          "$date": "2025-09-16T03:23:51.145Z"
        },
      },
      {

        "schemaTitle": "Customers / CRM",
        "dataTags": [
          "customer",
          "information"
        ],
        "description": "Track your customers and their information and build your basic CRM",
        "schemaContent": [
          {
            "title": "name",
            "value": "John",
            "type": "string"
          },
          {
            "title": "surname",
            "value": "Doe",
            "type": "string"
          },
          {
            "title": "email",
            "value": "johndoe@example.com",
            "type": "string",
            "validation": {
              "ref": "email"
            }
          },
          {
            "title": "phone",
            "value": "555-555-1234",
            "type": "phone",
            "validation": {
              "min": 10,
              "max": 11,
              "ref": "phone"
            }
          },
          {
            "title": "notes",
            "type": "string"
          },
          {
            "title": "address",
            "value": "123 Street",
            "type": "address"
          }
        ],
        "lastViewedAt": {
          "$date": "2025-09-18T21:14:50.571Z"
        },
        "updatedAt": {
          "$date": "2025-09-18T21:14:50.571Z"
        },
      }, {
        "_id": {
          "$oid": "64cdc09781f47b1bdf63f8c3"
        },
        "schemaTitle": "Services",
        "dataTags": [
          "service",
          "registration"
        ],
        "description": "Track your services and their details",
        "schemaContent": [
          {
            "title": "Service Name",
            "type": "string"
          },
          {
            "title": "Service Description",
            "type": "string",
            "ref": "multiline"
          },
          {
            "title": "Service Fee",
            "type": "number"
          },
          {
            "title": "Service ID",
            "type": "string"
          },
          {
            "title": "Service Start Date",
            "type": "date"
          },
          {
            "title": "Service Provider",
            "type": "string"
          },
          {
            "title": "Service Agreement (Upload)",
            "type": "string"
          },
          {
            "title": "Contract / Subscription Code",
            "type": "string"
          },
          {
            "title": "Service Registration Date",
            "type": "date"
          },
          {
            "title": "Additional Notes or Requirements",
            "type": "string",
            "validation": {
              "ref": "multiline"
            }
          }
        ],
        "lastViewedAt": {
          "$date": "2025-09-16T03:23:51.145Z"
        },
        "updatedAt": {
          "$date": "2025-09-16T03:23:51.145Z"
        },
        "viewCount": 1
      }
    ]

    return (
      <YStack gap={10}>
        {/* Connect Database Card */}
        <View style={styles.modernRow}>
          <Pressable
            style={[
              cardStyle,
              {
                flex: 1,
                minHeight: 220,
                borderWidth: 2,
                borderColor: 'rgba(59, 130, 246, 0.3)', // Blue border for database connection
                borderStyle: 'dashed',
                ...(Platform.OS === 'web' && {
                  transition: 'all 0.3s ease',
                }),
              }
            ]}
            onPress={() => setShowDatabaseForm(true)}
          >
            {/* Beta Badge */}
            <View style={{
              position: 'absolute',
              top: 10,
              right: -30,
              backgroundColor: 'rgb(239, 68, 68)',
              paddingVertical: 4,
              paddingHorizontal: 30,
              borderRadius: 8,
              transform: [{ rotate: '45deg' }],
              zIndex: 10,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.2,
              shadowRadius: 4,
              elevation: 5,
            }}>
              <SizableText fontSize={12} color="white" fontWeight="bold">
                BETA
              </SizableText>
            </View>

            {/* Title */}
            <Text
              adjustsFontSizeToFit={false}
              numberOfLines={2}
              style={[
                styles.modernCardTitle,
                {
                  textAlign: 'center',
                  marginBottom: 12,
                  flexShrink: 0,
                  color: getModernTextColor(theme, 'primary'),
                }
              ]}
            >
              Connect Database
            </Text>

            {/* Icon Container */}
            <View
              style={{
                height: 100,
                alignItems: 'center',
                justifyContent: 'center',
                marginVertical: 8,
              }}
            >
              <LottieView
                style={{
                  width: '80%',
                  height: '100%',
                }}
                source={require('@assets/animations/37211-google-icons-forms.json')}
                autoPlay
                loop={true}
              />
            </View>

            {/* Description */}
            <Text
              adjustsFontSizeToFit={false}
              numberOfLines={2}
              style={[
                styles.modernFieldLabel,
                {
                  textAlign: 'center',
                  marginTop: 8,
                  color: getModernTextColor(theme, 'muted'),
                  fontStyle: 'italic',
                  textTransform: 'none',
                  letterSpacing: 0,
                }
              ]}
            >
              Connect PostgreSQL, MySQL, MongoDB, or SQLite
            </Text>
          </Pressable>
        </View>
        <View style={styles.modernRow}>
          <Pressable
            style={[
              cardStyle,
              {
                flex: 1,
                minHeight: 220,
                borderWidth: 2,
                borderColor: 'rgba(139, 69, 19, 0.2)', // Brown border for blank canvas
                borderStyle: 'dashed',
                ...(Platform.OS === 'web' && {
                  transition: 'all 0.3s ease',
                }),
              }
            ]}
            onPress={() => handleBlankCanvasSelection()}
          >
          {/* Title */}
          <Text
            adjustsFontSizeToFit={false}
            numberOfLines={2}
            style={[
              styles.modernCardTitle,
              {
                textAlign: 'center',
                marginBottom: 12,
                flexShrink: 0,
                color: getModernTextColor(theme, 'primary'),
              }
            ]}
          >
            Blank Canvas
          </Text>

          {/* Stats Placeholder - Empty for blank canvas */}
          <XStack justifyContent="center" alignItems="center" marginBottom={16} gap={16}>
            <SizableText
              size="$2"
              color={getModernTextColor(theme, 'muted')}
              fontStyle="italic"
            >
              Start from scratch
            </SizableText>
          </XStack>

          {/* Animation Container */}
          <View
            style={{
              height: 100,
              alignItems: 'center',
              justifyContent: 'center',
              marginVertical: 8,
            }}
          >
            <LottieView
              style={{
                width: '80%',
                height: '100%',
              }}
              source={require('@assets/animations/37211-google-icons-forms.json')}
              autoPlay
              loop={true}
            />
          </View>

          {/* Description */}
          <Text
            adjustsFontSizeToFit={false}
            numberOfLines={2}
            style={[
              styles.modernFieldLabel,
              {
                textAlign: 'center',
                marginTop: 8,
                color: getModernTextColor(theme, 'muted'),
                fontStyle: 'italic',
                textTransform: 'none',
                letterSpacing: 0,
              }
            ]}
          >
            Create your own data model
          </Text>
        </Pressable>
      </View>
        {initialSchemas.map((item) => (
          <RenderSchema quickStart highLight item={item} />
        ))}

        {/* Database Connections */}
        {databaseConnections.map((connection) => (
          <View key={connection._id} style={styles.modernRow}>
            <Pressable
              style={[
                cardStyle,
                {
                  flex: 1,
                  minHeight: 220,
                  borderWidth: 2,
                  borderColor: connection.status === 'active'
                    ? 'rgba(16, 185, 129, 0.3)'
                    : 'rgba(239, 68, 68, 0.3)',
                  ...(Platform.OS === 'web' && {
                    transition: 'all 0.3s ease',
                  }),
                }
              ]}
              onPress={() => {
                schemaImportRef.current?.openModal(connection._id, connection.name);
              }}
            >
              {/* Title */}
              <Text
                adjustsFontSizeToFit={false}
                numberOfLines={2}
                style={[
                  styles.modernCardTitle,
                  {
                    textAlign: 'center',
                    marginBottom: 12,
                    flexShrink: 0,
                    color: getModernTextColor(theme, 'primary'),
                  }
                ]}
              >
                {connection.name}
              </Text>

              {/* Database Type Badge */}
              <XStack justifyContent="center" alignItems="center" marginBottom={16} gap={16}>
                <SizableText
                  size="$2"
                  color={getModernTextColor(theme, 'muted')}
                  fontStyle="italic"
                >
                  {connection.type.toUpperCase()}
                </SizableText>
              </XStack>

              {/* Animation Container */}
              <View
                style={{
                  height: 100,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginVertical: 8,
                }}
              >
                <LottieView
                  style={{
                    width: '80%',
                    height: '100%',
                  }}
                  source={require('@assets/animations/37211-google-icons-forms.json')}
                  autoPlay
                  loop={true}
                />
              </View>

              {/* Description */}
              <Text
                adjustsFontSizeToFit={false}
                numberOfLines={2}
                style={[
                  styles.modernFieldLabel,
                  {
                    textAlign: 'center',
                    marginTop: 8,
                    color: getModernTextColor(theme, 'muted'),
                    fontStyle: 'italic',
                    textTransform: 'none',
                    letterSpacing: 0,
                  }
                ]}
              >
                {connection.database} @ {connection.host}
              </Text>
            </Pressable>
          </View>
        ))}

      </YStack>
    )
  }

  const RenderSchema = ({ item, quickStart = false, highLight = false }: {
    item: any;
    quickStart?: boolean;
    highLight?: boolean;
  }) => {
    const isSelected = currentTitle === item.schemaTitle;
    const cardStyle = getModernCardStyle(theme, 'medium');

    return (
      <View style={styles.modernRow}>
        <Pressable
          style={[
            cardStyle,
            {
              flex: 1,
              minHeight: 220,
              backgroundColor: isSelected
                ? 'rgb(90,192,234)'
                : cardStyle.backgroundColor,
              borderWidth: highLight ? 4 : 2,
              borderColor: highLight
                ? 'rgba(90, 192, 234, 0.4)'
                : isSelected
                  ? 'rgba(90, 192, 234, 0.4)'
                  : 'rgba(0, 0, 0, 0.06)',
              ...(Platform.OS === 'web' && {
                transition: 'all 0.3s ease',
                transform: isSelected ? 'scale(1.02)' : 'scale(1)',
              }),
            }
          ]}
          onPress={() => handleSchemaSelection(item)}
        >
          {/* Quickstart Badge */}
          {quickStart && (
            <View style={{
              position: 'absolute',
              top: 10,
              right: -30,
              backgroundColor: 'rgb(90, 192, 234)',
              paddingVertical: 4,
              paddingHorizontal: 30,
              borderRadius: 8,
              transform: [{ rotate: '45deg' }],
              zIndex: 10,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.2,
              shadowRadius: 4,
              elevation: 5,
            }}>
              <SizableText fontSize={12} color="white" fontWeight="bold">
                Quickstart
              </SizableText>
            </View>
          )}
          {/* Schema Title */}
          <SizableText
            adjustsFontSizeToFit={false}
            numberOfLines={3}
            style={[
              styles.modernCardTitle,
              {
                textAlign: 'center',
                marginBottom: 12,
                flexShrink: 0,
                color: isSelected
                  ? 'white'
                  : getModernTextColor(theme, 'primary'),
              }
            ]}
          >
            {item.schemaTitle}
          </SizableText>

          {/* Usage Stats Row */}
          <XStack justifyContent="center" alignItems="center" marginBottom={16} gap={16}>
            <XStack alignItems="center" gap={6}>
              <FontAwesome
                name="eye"
                size={14}
                color={isSelected
                  ? 'rgba(255, 255, 255, 0.9)'
                  : getModernTextColor(theme, 'secondary')}
              />
              <SizableText
                size="$3"
                fontWeight="600"
                color={isSelected
                  ? 'rgba(255, 255, 255, 0.9)'
                  : getModernTextColor(theme, 'secondary')}
              >
                {item.viewCount || 0}
              </SizableText>
            </XStack>

            <View style={{
              width: 1,
              height: 16,
              backgroundColor: isSelected
                ? 'rgba(255, 255, 255, 0.3)'
                : 'rgba(0, 0, 0, 0.1)'
            }} />

            <XStack alignItems="center" gap={6}>
              <FontAwesome
                name="hand-pointer-o"
                size={14}
                color={isSelected
                  ? 'rgba(255, 255, 255, 0.9)'
                  : getModernTextColor(theme, 'secondary')}
              />
              <SizableText
                size="$3"
                fontWeight="600"
                color={isSelected
                  ? 'rgba(255, 255, 255, 0.9)'
                  : getModernTextColor(theme, 'secondary')}
              >
                {item.interactionCount || 0}
              </SizableText>
            </XStack>
          </XStack>
          {/* Tags */}
          <Text
            adjustsFontSizeToFit={false}
            numberOfLines={2}
            style={[
              styles.modernFieldLabel,
              {
                textAlign: 'center',
                marginTop: 8,
                color: isSelected
                  ? 'rgba(255, 255, 255, 0.8)'
                  : getModernTextColor(theme, 'muted'),
                fontStyle: 'italic',
                textTransform: 'none',
                letterSpacing: 0,
              }
            ]}
          >
            {Array.isArray(item.dataTags) ? item.dataTags.join(' • ') : item.dataTags}
          </Text>

          {/* Animation Container */}
          <View
            style={{
              height: 100,
              alignItems: 'center',
              justifyContent: 'center',
              marginVertical: 8,
            }}
          >
            <LottieView
              style={{
                width: '80%',
                height: '100%',
              }}
              source={require('@assets/animations/37211-google-icons-forms.json')}
              autoPlay
              loop={true}
            />
          </View>
          {
            Platform.OS === 'web' && (
              <SizableText textAlign="center" fontSize={14} color={getModernTextColor(theme, 'muted')} fontStyle="italic">{item.description}</SizableText>
            )
          }

        </Pressable>
      </View>
    )
  }


  return (
    <DefaultScreen layoutCallback={(layout) => { setScreenData(layout) }} aiSearch aiCallback={async (text) => {
      try {
        const response = await noMonitor.post('/ai/create-schema', { description: text }).then(res => res.data);

        if (response) {
          parseSchema(response.raw, dispatch).then(() => {
            navigation.navigate('DataHandler');
          })
        }

      } catch (err) {
        store.dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'An error occured while trying to generate a schema from the AI model, please try again later', type: 'error' } })
      }
    }} screenTitle={currentTitle} style={{ paddingTop: 10, flex: 1, marginLeft: 15, paddingBottom: 50 }} backButton={true} scrollDisabled={true} searchBar={selectedSchema ? undefined : { callback: handleSearch, placeHolder: "Search for or describe your model" }}>
      <XStack flexWrap='wrap' style={[styles.contentContainer, { flexDirection: 'row', gap: 30, alignSelf: 'stretch', flex: 5, backgroundColor: 'transparent', top: 15, minWidth: '100%' }]}>
        {(
            <Animatable.View
          transition={['flex', 'opacity', 'zIndex']}
          style={{
            borderRadius: 24,
            flex: screenData.width < 850 && !selectedSchema ? 1 : (selectedSchema ? 5 : 1),
            backgroundColor: 'transparent',
            gap: 20,
            opacity: screenData.width < 850 && selectedSchema ? 0 : 1,
            zIndex: screenData.width < 850 && selectedSchema ? -1 : 2
          }}
        >
          <Card style={{
            flex: 3,
            flexGrow: 5,
            alignSelf: "stretch",
            backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(40, 40, 40, 0.95)',
              borderRadius: 24,
            minWidth: 300,
            paddingTop: 15,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: theme ? 0.15 : 0.25,
            shadowRadius: 16,
            elevation: 10,
            borderWidth: 1,
            borderColor: theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
          }}>
            <H6 textAlign='center' style={{
              marginVertical: 16,
              fontSize: 20,
              fontWeight: 'bold',
              color: 'rgb(90, 192, 234)'
            }}>
              Default {t('Templates')}
            </H6>
            {isLoading ?
              <LottieView style={{ flex: 1, height: 180 }} source={require('@assets/animations/leaf_Loader.json')} />
              :
              <View style={{
                  height: Platform.OS === 'web' ? screenData.height * 0.8 : 500,
                  backgroundColor: 'transparent',
                minWidth: 300
              }}>
                <FlatList
                key={(screenData.width < 850 ? 'horizontal' : 'vertical') + defaultSchemas?.length}
                  data={defaultSchemas}
                  horizontal={screenData.width < 850}
                  keyExtractor={(item) => item._id}
                    showsVerticalScrollIndicator={true}
                    showsHorizontalScrollIndicator={screenData.width < 850}
                  initialNumToRender={10}
                  contentContainerStyle={[styles.modernGrid]}
                  renderItem={({ item, index }) => (<RenderSchema item={item} />)}
                  ListHeaderComponent={headerItem}
                    nestedScrollEnabled={true}
                    style={{ flex: 1 }}
                />
              </View>
            }
          </Card>
          {
            Platform.OS != 'web' && !selectedSchema && !isTablet &&
            <View style={{
              flex: 1,
            }}>
              {
                !hideTutorial ?
                  <GlobalTutorial carouselData={carouselData} completeCallback={completeTutorialCallBack} />
                  :
                  <Animatable.View useNativeDriver animation={'slideInUp'} style={{
                    backgroundColor: 'transparent'
                  }}>
                    <View style={{
                      backgroundColor: theme ? 'rgba(248, 250, 252, 0.9)' : 'rgba(30, 30, 30, 0.9)',
                      borderRadius: 16,
                      padding: 20,
                      alignItems: 'center'
                    }}>
                      <Text style={{
                        fontSize: 16,
                        fontWeight: '600',
                        color: 'rgb(90, 192, 234)',
                        marginBottom: 8
                      }}>
                        Need Help?
                      </Text>
                      <Text style={{
                        fontSize: 14,
                        textAlign: 'center',
                        color: theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)'
                      }}>
                        Select a template to start building your data schema
                      </Text>
                    </View>
                  </Animatable.View>
              }
            </View>
          }
        </Animatable.View>
          )
        }
          {
            (Platform.OS === 'web' || isTablet) && !selectedSchema && screenData.width > 850 &&
            <Animatable.View useNativeDriver style={{
              flex: 1,
              justifyContent: 'center',
              alignSelf: 'stretch',
              padding: 10,
              paddingRight: 20,
              flexGrow: 1,
              flexBasis: "50%",
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(40, 40, 40, 0.95)',
              borderRadius: 24,
              marginHorizontal: 10,
              shadowColor: '#000',
              elevation: 5,
              borderWidth: 1,
              borderColor: theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
            }}>
              {
                !hideTutorial ?
                  <View >
                    <GlobalTutorial carouselData={carouselData} customHeight={Platform.OS != 'web' ? 300 : 400} customWidth={Platform.OS != 'web' ? 230 : 400} showArrows completeCallback={completeTutorialCallBack} />
                  </View>
                  :
                  <>
                    {
                      helpOpen &&
                      <View style={{
                        backgroundColor: theme ? 'rgba(248, 250, 252, 0.9)' : 'rgba(30, 30, 30, 0.9)',
                          borderRadius: 16,
                        alignItems: 'center',
                        margin: 20
                      }}>
                        <Text style={{
                          fontSize: 20,
                          fontWeight: 'bold',
                          color: 'rgb(90, 192, 234)',
                          marginBottom: 12
                        }}>
                          Get Started
                        </Text>
                        <Text style={{
                          fontSize: 16,
                          textAlign: 'center',
                          color: theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                          lineHeight: 24
                        }}>
                          Choose a template from the left panel to start building your custom data schema, or create a blank canvas for complete customization.
                        </Text>
                      </View>
                    }
                  </>
              }
            </Animatable.View>
        }
            {
              selectedSchema &&
          <Animatable.View transition={'flex'} style={{ backgroundColor: 'transparent' }}>
              <>
                {loadingSchema ?
                  <View style={[
                    getModernCardStyle(theme, 'large'),
                    {
                      flex: 1,
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginVertical: "20%",
                      marginHorizontal: "10%",
                      minHeight: 350,
                      top: Platform.OS === 'web' ? -180 : undefined,
                    }
                  ]}>
                    <LottieView
                      source={require('@assets/animations/spin-leaf-loader.json')}
                      style={{
                        width: 120,
                        height: 120,
                      }}
                      speed={2}
                      autoPlay
                      loop
                    />
                    <H6 style={[
                      styles.modernCardTitle,
                      {
                        marginTop: 24,
                        textAlign: 'center',
                        color: getModernTextColor(theme, 'secondary'),
                      }
                    ]}>
                      Loading schema template...
                    </H6>
                  </View>
                  :
                  <View style={[
                    getModernCardStyle(theme, 'large'),
                    {
                      flex: 1,
                      padding: 0,
                      marginHorizontal: -20,
                      marginTop: 0,
                      overflow: 'hidden',
                    }
                  ]}>
                    {/* Modern Header */}
                    <View style={[
                      styles.modernRow,
                      {
                        minHeight: 80,
                        paddingHorizontal: 24,
                        paddingVertical: 16,
                        backgroundColor: theme ? 'rgba(248, 250, 252, 0.98)' : 'rgba(30, 30, 30, 0.98)',
                        flexDirection: 'column-reverse',
                        borderBottomWidth: 1,
                        borderBottomColor: theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }
                    ]}>
                      {/* Action Buttons Section - Pill-shaped container */}
                      <View
                        style={{
                          flex: 1,
                          backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(26, 26, 26, 0.95)',
                          borderRadius: 9999,
                          borderWidth: 1,
                          borderColor: theme ? '#e5e7eb' : '#374151',
                          paddingHorizontal: 12,
                          paddingVertical: 8,
                          shadowColor: '#000',
                          shadowOffset: Platform.OS === 'web' ? undefined : { width: 0, height: 2 },
                          shadowOpacity: Platform.OS === 'web' ? undefined : 0.1,
                          shadowRadius: Platform.OS === 'web' ? undefined : 4,
                          ...(Platform.OS === 'web' ? { boxShadow: '0 2px 4px rgba(0,0,0,0.1)' } : {}),
                          elevation: 4,
                          overflow: 'hidden',
                        }}
                      >
                        <XStack gap={8} alignItems="center" flex={1} flexWrap="wrap">
                          <ActionButton
                            row
                            noBackground
                            pill
                            showLabel={true}
                            removeWidthConstraint={true}
                            action={{
                              function: () => { setSelectedSchema(false); setCurrentTitle(""); setSelectedSchemaItem(null); },
                              icon: 'left',
                              label: t('Back'),
                              labelColor: getModernTextColor(theme, 'primary'),
                            }}
                          />
                          <ActionButton
                            row
                            noBackground
                            pill
                            showLabel={true}
                            removeWidthConstraint={true}
                            action={{
                              function: () => { setFlip(!flip) },
                              icon: 'eye',
                              label: t('Preview'),
                              labelColor: getModernTextColor(theme, 'primary'),
                            }}
                          />
                          <ActionButton
                            row
                            noBackground
                            pill
                            showLabel={true}
                            removeWidthConstraint={true}
                            action={{
                              function: () => handleAIValidation(),
                              icon: 'check',
                              label: t('AI Validate'),
                              labelColor: 'rgb(90, 192, 234)',
                            }}
                          />
                          <ActionButton
                            row
                            pill
                            noBackground
                            showLabel={true}
                            removeWidthConstraint={true}
                            action={{
                              function: () => handleAIFieldSuggestion(),
                              icon: 'bulb',
                              label: t('AI Suggest'),
                              labelColor: 'rgb(90, 192, 234)',
                            }}
                          />
                          <ActionButton
                            row
                            noBackground
                            pill
                            showLabel={true}
                            removeWidthConstraint={true}
                            action={{
                              function: () => handleProceedToEdit(),
                              icon: 'edit',
                              label: t('Edit'),
                              labelColor: getModernTextColor(theme, 'primary'),
                            }}
                          />
                          <ActionButton
                            row
                            pill
                            noBackground
                            showLabel={true}
                            removeWidthConstraint={true}
                            action={{
                              function: () => handleSaveData(),
                              icon: 'save',
                              label: t('Save'),
                              labelColor: getModernTextColor(theme, 'primary'),
                            }}
                          />
                        </XStack>
                      </View>

                      {/* Schema Title Section */}
                      <XStack alignSelf='stretch' justifyContent='space-between' style={{ flex: 1 }}>
                        <H5
                          adjustsFontSizeToFit={false}
                          numberOfLines={2}
                          style={[
                            styles.modernSectionTitle,
                            {
                              color: 'rgb(90, 192, 234)',
                              marginBottom: 0,
                              flex: 3
                            }
                          ]}
                        >
                          {currentTitle}
                        </H5>
                        {selectedSchemaItem && (
                          <XStack marginLeft={"5%"} flex={1} gap={12} alignItems="center">
                            <XStack alignItems="center" gap={4}>
                              <FontAwesome
                                name="eye"
                                size={12}
                                color={getModernTextColor(theme, 'muted')}
                              />
                              <SizableText
                                size="$2"
                                color={getModernTextColor(theme, 'muted')}
                              >
                                {selectedSchemaItem.viewCount || 0}
                              </SizableText>
                            </XStack>
                            <XStack alignItems="center" gap={4}>
                              <FontAwesome
                                name="hand-pointer-o"
                                size={12}
                                color={getModernTextColor(theme, 'muted')}
                              />
                              <SizableText
                                size="$2"
                                color={getModernTextColor(theme, 'muted')}
                              >
                                {selectedSchemaItem.interactionCount || 0}
                              </SizableText>
                            </XStack>
                          </XStack>
                        )}
                      </XStack>
                    </View>

                    {/* Content Area */}
                    <View style={[styles.modernMainContent, { flex: 1 }]}>
                      <FlipCard
                        clickable={false}
                        flipVertical={true}
                        flipHorizontal={false}
                        flip={flip}
                        style={{ flex: 1, minHeight: 400 }}>
                        <View style={[
                          styles.face,
                          getModernCardStyle(theme, 'medium'),
                          {
                            flex: 1,
                            backgroundColor: 'transparent',
                          }
                        ]}>
                          <GenerateSchemaForm removeSubmit schema={dataTracker.dataHeld} />
                        </View>
                        <ScrollView style={[
                          styles.back,
                          getModernCardStyle(theme, 'medium'),
                          {
                            borderWidth: 0,
                            shadowOpacity: 0,
                            backgroundColor: 'transparent',
                          }
                        ]}>
                          <SchemaPreviewer interaction={false} />
                        </ScrollView>
                      </FlipCard>
                    </View>
                  </View>
                }
              </>
          </Animatable.View>

        }

      </XStack>

      {/* Database Connection Form Modal */}
      {showDatabaseForm && (
        <View style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}>
          <View style={{
            width: Platform.OS === 'web' ? 600 : '90%',
            maxWidth: Platform.OS === 'web' ? 800 : '100%',
            maxHeight: Platform.OS === 'web' ? '80%' : '90%',
          }}>
            <DatabaseConnectionForm
              initialData={editingConnection}
              scope="user"
              onSuccess={(connection) => {
                setShowDatabaseForm(false);
                setEditingConnection(null);
                loadDatabaseConnections();
                dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Database connection created successfully', type: 'success' } });
              }}
              onCancel={() => {
                setShowDatabaseForm(false);
                setEditingConnection(null);
              }}
            />
          </View>
        </View>
      )}

      {/* Schema Import Modal */}
      <SchemaImportModal
        ref={schemaImportRef}
        onImportComplete={(schemas) => {
          if (schemas && schemas.length > 0) {
            navigation.navigate('DataHandler');
          }
        }}
      />
    </DefaultScreen>
  );
}

export default CreateDataScreen;